// Story 10.6 — the logged-out `/u/{handle}` public collection page. Shows a
// collector's PUBLISHED items (opened certificates + sealed dossiers) as the
// same artifacts they see in their own vault. Empty published set renders an
// empty state (not a 404); an unknown / private / anonymized handle is the
// single 404 bucket. Demo-grade: SPA-only, does not unfurl.

import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PatronageCertificate, SealedDossier, type SealedDossierData } from "@greenhold/components";

import { usePublicCollection } from "../../features/public-collection/usePublicCollection";
import type {
  PublicCollectionItem,
  PublicShareDto,
  PublicSealedDossierDto,
} from "../../features/public-collection/publicCollectionTypes";
import { publicCertProps } from "./PublicCertificatePage";
import { PublicShell, PublicNotFound, PublicLoading } from "./PublicShell";

function sealedData(dto: PublicSealedDossierDto): SealedDossierData {
  return {
    id: dto.sealed_dossier_id,
    drop_id: dto.drop_id,
    edition_number: dto.edition_number,
    portfolio_title: dto.portfolio_title,
    drop_status: dto.drop_status,
    retiring_at: dto.retiring_at,
    seal_color: dto.seal_color,
  };
}

function ItemCard({ item }: { item: PublicCollectionItem }) {
  if (item.kind === "certificate") {
    const dto: PublicShareDto = item;
    return (
      <li className="flex flex-col items-center gap-2">
        <Link to={`/c/${dto.share_id}`} className="block w-32 rounded-md">
          <PatronageCertificate width={128} {...publicCertProps(dto)} />
        </Link>
        <span className="w-full max-w-[8rem] truncate text-center text-xs opacity-70">
          {dto.snapshot_display_name}
        </span>
      </li>
    );
  }
  const dto: PublicSealedDossierDto = item;
  return (
    <li className="flex flex-col items-center gap-2">
      <Link to={`/d/${dto.sealed_dossier_id}`} className="block w-32 rounded-md">
        <SealedDossier
          dossier={sealedData(dto)}
          size="md"
          game={dto.portfolio_theme ?? undefined}
        />
      </Link>
      <span className="w-full max-w-[8rem] truncate text-center text-xs opacity-70">
        {dto.portfolio_title}
      </span>
    </li>
  );
}

export function PublicCollectionPage() {
  const { handle = "" } = useParams();
  const [page, setPage] = useState(1);
  const query = usePublicCollection(handle, page);

  if (query.isLoading) return <PublicLoading />;
  if (query.data?.notFound || !query.data?.data) {
    return (
      <PublicNotFound
        title="No collection here"
        detail="This handle doesn't exist, or its owner keeps their profile private."
      />
    );
  }

  const profile = query.data.data;
  const name = profile.display_name || `@${profile.handle}`;
  return (
    <PublicShell>
      <header className="mb-8 flex items-center gap-4">
        {profile.image_signed_url ? (
          <img
            src={profile.image_signed_url}
            alt=""
            className="h-16 w-16 rounded-full object-cover"
          />
        ) : (
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-gold-500/20 font-heading text-2xl text-brand-gold-300">
            {name.replace(/^@/, "").charAt(0).toUpperCase()}
          </span>
        )}
        <div>
          <h1 className="font-heading text-3xl font-bold">{name}</h1>
          {profile.public_bio && (
            <p className="mt-1 text-sm opacity-70">{profile.public_bio}</p>
          )}
          <p className="mt-1 text-xs opacity-50">
            {profile.total_published}{" "}
            {profile.total_published === 1 ? "item" : "items"} published
          </p>
        </div>
      </header>

      {profile.items.length === 0 ? (
        <p className="text-sm opacity-60">
          Nothing published yet — check back soon.
        </p>
      ) : (
        <ul className="grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-4">
          {profile.items.map((item) => (
            <ItemCard
              key={
                item.kind === "certificate"
                  ? item.share_id
                  : item.sealed_dossier_id
              }
              item={item}
            />
          ))}
        </ul>
      )}

      {(page > 1 || profile.has_next) && (
        <div className="mt-8 flex items-center justify-center gap-4">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded border border-brand-forest-700 px-3 py-1 text-sm disabled:opacity-40"
          >
            ← Prev
          </button>
          <span className="text-xs opacity-60">Page {page}</span>
          <button
            type="button"
            disabled={!profile.has_next}
            onClick={() => setPage((p) => p + 1)}
            className="rounded border border-brand-forest-700 px-3 py-1 text-sm disabled:opacity-40"
          >
            Next →
          </button>
        </div>
      )}
    </PublicShell>
  );
}
